import type {
  FormattedNewWager,
  FormattedNewWagers,
  FormattedNewWagersResponse,
  NewWagersRequest,
  NewWagersResponse,
  RefundNewWagerRequest
} from '@library/types/newWagers';
import { concat, mergeDeepWith } from 'ramda';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { transformNewWagers } from '@bo-utils/newWagers';

const { member } = apiPaths;

export const newWagersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNewWagerById: build.query<
      FormattedNewWagersResponse | undefined,
      NewWagersRequest
    >({
      query: ({ id, userId }) => ({
        url: `${member}/${userId}/new_wagers`,
        method: 'GET',
        params: {
          id,
          page: 1,
          per_page: 1
        },
        headers: defaultHeaders
      }),
      transformResponse: (response: NewWagersResponse, _, { page, perPage }) =>
        transformNewWagers(response, page, perPage),
      providesTags: ['NewWager']
    }),
    getNewWagers: build.query<
      FormattedNewWagersResponse | undefined,
      NewWagersRequest
    >({
      query: ({
        id,
        resultEq,
        sportEq,
        startDate,
        endDate,
        leagueId,
        // playerNameCont,
        projectionStatusEq,
        sort,
        page = 1,
        perPage = 10,
        userId
      }: NewWagersRequest) => ({
        url: `${member}/${userId}/new_wagers`,
        method: 'GET',
        params: {
          id,
          result_eq: resultEq,
          sport_eq: sportEq,
          created_at_gteq: startDate,
          created_at_lteq: endDate,
          predictions_projection_league_id_in: leagueId,
          // predictions_projection_new_player_name_cont: playerNameCont,
          predictions_projection_status_eq: projectionStatusEq,
          sort,
          page,
          per_page: perPage
        },
        headers: defaultHeaders
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedNewWager, b: FormattedNewWager) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as FormattedNewWagers;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedNewWagersResponse;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg), // force refetch when query args change
      transformResponse: (response: NewWagersResponse, _, { page, perPage }) =>
        transformNewWagers(response, page, perPage),
      providesTags: ['NewWagers']
    }),
    refundNewWager: build.mutation<void, RefundNewWagerRequest>({
      query: ({ memberId, newWagerId }) => ({
        url: `${member}/${memberId}/new_wagers/${newWagerId}/refund`,
        method: 'POST',
        headers: defaultHeaders
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Only invalidate tags if the mutation was successful
          dispatch(newWagersApi.util.invalidateTags(['NewWager', 'NewWagers']));
        } catch {
          // Do nothing on error - use error prop from mutation result
        }
      }
    })
  })
});

export const {
  useGetNewWagerByIdQuery,
  useGetNewWagersQuery,
  useRefundNewWagerMutation
} = newWagersApi;
